import { useEffect, useState } from "react";
import { Check, Trash2 } from "lucide-react";
import { PhotoPicker } from "@/components/PhotoPicker";
import { AuthedImage } from "@/components/AuthedImage";
import { fmtTime } from "@/lib/api";

const QtyInput = ({ item, locked, onQty }) => {
  const [val, setVal] = useState(item.qty_counted ?? "");
  useEffect(() => { setVal(item.qty_counted ?? ""); }, [item.qty_counted]);
  const commit = () => {
    const n = val === "" ? null : parseInt(val, 10);
    if (n !== item.qty_counted && (n === null || !Number.isNaN(n))) onQty(n);
  };
  const counted = item.qty_counted;
  const tone = counted == null ? "border-[#2A2E39]" : counted === item.expected_qty ? "border-[#00E676] text-[#00E676]" : "border-[#FFB300] text-[#FFB300]";
  return (
    <div className="flex items-center gap-1">
      <input
        type="number" inputMode="numeric" min="0" value={val} disabled={locked}
        onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        placeholder="0" data-testid={`item-qty-input-${item.id}`}
        className={`w-16 h-12 rounded-md bg-[#0B0C10] border text-center font-mono text-base focus:outline-none focus:border-[#A9B26A] disabled:opacity-60 ${tone}`}
      />
      <span className="font-mono text-xs text-slate-500" data-testid={`item-expected-qty-${item.id}`}>/{item.expected_qty}</span>
    </div>
  );
};

export const ItemRow = ({ item, locked, onToggle, onQty, onPhoto, onDeletePhoto, onDelete }) => (
  <li className={`item-row rounded-lg border px-3 py-2 transition-colors ${item.checked ? "border-[#00E676]/40 bg-[#00E676]/[0.06]" : "border-[#2A2E39] bg-[#15181E]"}`} data-testid={`item-row-${item.id}`}>
    <div className={`flex items-center gap-x-3 gap-y-1 ${item.expected_qty != null ? "flex-wrap sm:flex-nowrap" : ""}`}>
      <button type="button" disabled={locked} onClick={onToggle} data-testid={`item-check-toggle-${item.id}`} aria-pressed={item.checked}
        className={`${item.expected_qty != null ? "basis-full sm:basis-auto" : ""} flex-1 min-w-0 flex items-center gap-3 min-h-[48px] text-left disabled:cursor-not-allowed`}>
        <span className={`shrink-0 w-8 h-8 rounded-md border-2 grid place-items-center transition-colors ${item.checked ? "bg-[#00E676] border-[#00E676] text-black check-pop" : "border-slate-500"}`}>
          {item.checked && <Check className="w-5 h-5" strokeWidth={3} />}
        </span>
        <span className={`min-w-0 break-words text-[15px] leading-snug ${item.checked ? "text-slate-300" : "text-white"}`} data-testid={`item-name-${item.id}`}>{item.name}</span>
      </button>
      <div className={`${item.expected_qty != null ? "ml-11 sm:ml-0" : ""} flex items-center gap-2`}>
        {item.expected_qty != null && <QtyInput item={item} locked={locked} onQty={onQty} />}
        <PhotoPicker disabled={locked} onFile={onPhoto} testId={`item-photo-btn-${item.id}`} />
        {!locked && (
          <button type="button" onClick={onDelete} data-testid={`item-delete-btn-${item.id}`} className="tap-target grid place-items-center rounded-md text-slate-500 hover:text-[#FF3838] hover:bg-[#FF3838]/10 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
    {(item.photos.length > 0 || item.checked_by) && (
      <div className="pl-11 pb-1 pt-1 space-y-2">
        {item.photos.length > 0 && (
          <div className="flex flex-wrap gap-3 pt-1">
            {item.photos.map((p) => (
              <AuthedImage key={p.id} fileId={p.id} className="w-16 h-16" testId={`item-photo-${p.id}`}
                caption={`Foto por ${p.uploaded_by?.name} às ${fmtTime(p.uploaded_at)}`}
                onDelete={locked ? null : () => onDeletePhoto(p.id)} />
            ))}
          </div>
        )}
        {item.checked_by && (
          <p className="font-mono text-[11px] text-slate-500" data-testid={`item-checked-by-${item.id}`}>
            ✓ {item.checked_by.name} · {fmtTime(item.checked_at)}
          </p>
        )}
      </div>
    )}
  </li>
);
